import { NotFoundError } from '@cian/peperrors/shared';

import { RuntimeLoadable } from './RuntimeLoadable.js';
import { ERuntimeType, type IAPIRuntimeManifest } from '../../../shared/manifest/index.js';

import type { APIRuntimeBuilder, IAPIHandler, IAPIParametersHandler } from '../../bundles/interfaces.js';
import type { IAPIRuntime } from '../../runtime/interfaces.js';
import type { IRuntimeContext } from '../context/index.js';
import type { IImageLoadable } from '../image/interfaces.js';

export class APIRuntime extends RuntimeLoadable<APIRuntimeBuilder> implements IAPIRuntime {
  private readonly context: IRuntimeContext;
  private methods: Map<string, IAPIParametersHandler<unknown, unknown>['handler'] | IAPIHandler<unknown>['handler']> =
    new Map();

  public constructor(manifest: IAPIRuntimeManifest, image: IImageLoadable, context: IRuntimeContext) {
    super(manifest, image);

    this.context = context;
  }

  public get type(): ERuntimeType.API {
    return ERuntimeType.API;
  }

  public async call<Parameters = undefined, Result = undefined>(
    method: string,
    parameters?: Parameters | undefined,
  ): Promise<Result> {
    const fn = this.methods.get(method);

    if (!fn) {
      throw new NotFoundError({
        message: `Метод ${method} отсутствует в API Runtime (${this.context.microfrontend.name}#${this.context.image.version}#${this.name})`,
        domain: 'mf-registry.browser.api-runtime.call',
      });
    }

    let result: Result;

    if (parameters) {
      const safeParameters = JSON.parse(JSON.stringify(parameters));

      result = await (fn as IAPIParametersHandler<Parameters, Result>['handler'])(safeParameters);
    } else {
      result = await (fn as IAPIHandler<Result>['handler'])();
    }

    if (result === undefined) {
      return undefined as Result;
    }

    return JSON.parse(JSON.stringify(result));
  }

  protected afterBuilderLoaded(): void {
    for (const { path, handler } of this.runtimeBuilder) {
      this.methods.set(path, handler);
    }
  }
}
